import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Loader2, AlertCircle, RefreshCw, Users, X, RotateCcw, Ban, PackageOpen, Trash2,
} from 'lucide-react';
import { PageHeader, Button, Avatar } from '../ui-v2';
import { Modal, Input, Badge, EmptyState, Tooltip } from '../ui-v2.5';
import {
  listAreas, crearArea, actualizarArea, desactivarArea, anadirMiembroArea, quitarMiembroArea,
  eliminarAreaPermanente,
} from './api';
import { COLOR_AREA_DEFECTO } from './paletaAreas';
import ColorPicker from './components/ColorPicker';
import PersonaPickerMiembro from './components/PersonaPickerMiembro';
import EliminarModal from './components/EliminarModal';
import IncorporacionesTabs from './IncorporacionesTabs';

/**
 * Áreas — pantalla de administración (§3.5). Tarjetas con color, recuento y avatares de miembros;
 * editor en Modal (crear/editar/reactivar); desactivar con Modal danger que muestra los recursos
 * afectados si el backend devuelve 409; miembros con el selector adaptado (solo internos).
 *
 * DS: ui-v2.5 (Modal, Input, Badge, EmptyState) + Button/PageHeader/Avatar de ui-v2. toast global
 * (react-toastify) para éxito y error. El color se elige por swatches de la paleta del catálogo.
 */

// Extrae el cuerpo de error del backend ({ error, message, recursos?, id? }) de una excepción axios.
const cuerpoError = (e) => (e && e.response && e.response.data) || {};

export default function Areas() {
  const [areas, setAreas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [accionId, setAccionId] = useState(null); // área con acción en curso (reactivar desde tarjeta)
  const [editor, setEditor] = useState(null);      // { area|null } → modal abierto; null → cerrado
  const [aDesactivar, setADesactivar] = useState(null); // área objetivo del modal danger (desactivar)
  const [aEliminar, setAEliminar] = useState(null);     // área objetivo del modal danger (eliminar)

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      setAreas(await listAreas());
    } catch (e) {
      console.error('[incorp][areas] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Reactivar desde la tarjeta (área inactiva): PUT con activa=true, sin modal.
  async function reactivar(area) {
    setAccionId(area.id);
    try {
      await actualizarArea(area.id, {
        nombre: area.nombre, color: area.color, orden: area.orden, activa: true,
      });
      toast.success(`Área "${area.nombre}" reactivada.`);
      await cargar();
    } catch (e) {
      console.error('[incorp][areas] reactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el área.');
    } finally {
      setAccionId(null);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Áreas"
        subtitle="Áreas responsables de preparar los recursos de una incorporación."
        action={<Button onClick={() => setEditor({ area: null })}><Plus />Nueva área</Button>}
      />

      <IncorporacionesTabs active="areas" />

      {/* Contenido: carga / error / vacío / tarjetas */}
      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando áreas…
        </div>
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">No se pudieron cargar las áreas.</p>
          <Button variant="outline" onClick={cargar}><RefreshCw />Reintentar</Button>
        </div>
      ) : areas.length === 0 ? (
        <EmptyState
          variant="medium"
          icon={Users}
          title="Aún no hay áreas"
          description="Crea la primera área responsable de preparar recursos."
          action={<Button onClick={() => setEditor({ area: null })}><Plus />Nueva área</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((a) => (
            <AreaCard
              key={a.id}
              area={a}
              accion={accionId === a.id}
              onEditar={() => setEditor({ area: a })}
              onDesactivar={() => setADesactivar(a)}
              onReactivar={() => reactivar(a)}
              onEliminar={() => setAEliminar(a)}
            />
          ))}
        </div>
      )}

      {editor && (
        <AreaModal
          area={editor.area}
          onClose={() => setEditor(null)}
          onGuardado={() => { setEditor(null); cargar(); }}
        />
      )}

      {aDesactivar && (
        <DesactivarModal
          area={aDesactivar}
          onClose={() => setADesactivar(null)}
          onDesactivado={() => { setADesactivar(null); cargar(); }}
        />
      )}

      {aEliminar && (
        <EliminarModal
          titulo="Eliminar área"
          subtitulo={aEliminar.nombre}
          exitoMsg={`Área «${aEliminar.nombre}» eliminada permanentemente.`}
          eliminar={() => eliminarAreaPermanente(aEliminar.id)}
          onClose={() => setAEliminar(null)}
          onEliminado={() => { setAEliminar(null); cargar(); }}
        >
          Se eliminará el área <strong>«{aEliminar.nombre}»</strong> de forma permanente.{' '}
          {aEliminar.miembros_count > 0
            ? <>Se perderán sus <strong>{aEliminar.miembros_count} miembro(s)</strong> (la pertenencia al área, no las personas).</>
            : <>No tiene miembros.</>}{' '}
          Esta acción no se puede deshacer.
        </EliminarModal>
      )}
    </div>
  );
}

// ─────────────────────────── Tarjeta de área ───────────────────────────
function AreaCard({ area, accion, onEditar, onDesactivar, onReactivar, onEliminar }) {
  const miembros = area.miembros || [];
  const visibles = miembros.slice(0, 5);
  const resto = miembros.length - visibles.length;
  return (
    <div
      className={`rounded-v25-lg border border-v25-line bg-white p-4 shadow-v25-btn ${area.activa ? '' : 'opacity-70'}`}
      style={{ borderLeft: `4px solid ${area.color || '#94a3b8'}` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: area.color || '#94a3b8' }} />
            <h3 className="truncate text-sm font-semibold text-v25-ink-900">{area.nombre}</h3>
          </div>
          <p className="mt-0.5 text-xs text-v25-ink-500">Orden {area.orden}</p>
        </div>
        {!area.activa && <Badge tone="expired">Desactivada</Badge>}
      </div>

      {/* Miembros: avatares + recuento */}
      <div className="mt-3 flex items-center gap-2">
        {miembros.length === 0 ? (
          <span className="text-xs text-v25-ink-400">Sin miembros</span>
        ) : (
          <>
            <div className="flex -space-x-2">
              {visibles.map((m) => (
                <Avatar key={m.id} name={m.nombre} size="sm" className="ring-2 ring-white" />
              ))}
              {resto > 0 && (
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-v25-surface-raised text-[10px] font-medium text-v25-ink-500 ring-2 ring-white">
                  +{resto}
                </span>
              )}
            </div>
            <span className="text-xs text-v25-ink-500">
              {miembros.length} {miembros.length === 1 ? 'miembro' : 'miembros'}
            </span>
          </>
        )}
      </div>

      {/* Acciones — solo icono (el texto se salía de la tarjeta); mismos iconos que las tablas de
          Recursos/Puestos + title. Eliminar (permanente) separada y en rojo para no confundirla. */}
      <div className="mt-4 flex items-center justify-end gap-1 border-t border-v25-line pt-3">
        <Tooltip content="Editar"><Button variant="ghost" size="icon" onClick={onEditar}><Pencil /></Button></Tooltip>
        {area.activa ? (
          <Tooltip content="Desactivar"><Button variant="ghost" size="icon" onClick={onDesactivar}><Ban /></Button></Tooltip>
        ) : (
          <Tooltip content="Reactivar">
            <Button variant="ghost" size="icon" disabled={accion} onClick={onReactivar}>
              {accion ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            </Button>
          </Tooltip>
        )}
        <span className="mx-1 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />
        <Tooltip content="Eliminar permanentemente">
          <Button variant="ghost" size="icon" onClick={onEliminar} className="text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

// ─────────────────────────── Modal editor (crear/editar) ───────────────────────────
function AreaModal({ area, onClose, onGuardado }) {
  const esEdicion = !!(area && area.id);
  const [nombre, setNombre] = useState(area?.nombre || '');
  const [color, setColor] = useState(area?.color || COLOR_AREA_DEFECTO);
  const [orden, setOrden] = useState(area?.orden ?? 0);
  const [miembros, setMiembros] = useState(area?.miembros || []);
  const [guardando, setGuardando] = useState(false);
  const [errNombre, setErrNombre] = useState('');
  // Oferta de reactivación tras 409 nombre_duplicado_inactivo: { id, nombre }. Usa el id del 409.
  const [reactivable, setReactivable] = useState(null);
  const [miembroBusy, setMiembroBusy] = useState(false);

  const validar = () => {
    const n = nombre.trim();
    if (!n) { setErrNombre('El nombre es obligatorio.'); return null; }
    if (n.length > 60) { setErrNombre('Máximo 60 caracteres.'); return null; }
    const o = Number(orden);
    if (!Number.isInteger(o) || o < 0) { setErrNombre(''); toast.error('El orden debe ser un entero ≥ 0.'); return null; }
    setErrNombre('');
    return { nombre: n, color, orden: o };
  };

  async function guardar() {
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    setReactivable(null);
    try {
      if (esEdicion) {
        await actualizarArea(area.id, payload);
        toast.success('Área actualizada.');
      } else {
        await crearArea(payload);
        toast.success('Área creada.');
      }
      onGuardado();
    } catch (e) {
      const body = cuerpoError(e);
      if (body.error === 'nombre_duplicado_inactivo' && body.id) {
        // Ofrecer reactivar SIN una petición extra: el id viene en el propio 409.
        setReactivable({ id: body.id, nombre: payload.nombre });
      } else if (body.error === 'nombre_duplicado') {
        setErrNombre('Ya existe un área con ese nombre.');
      } else {
        console.error('[incorp][areas] guardar', e);
        toast.error(body.message || 'No se pudo guardar el área.');
      }
    } finally {
      setGuardando(false);
    }
  }

  // Reactivar el área desactivada homónima con el id del 409 (aplica también los valores del form).
  async function reactivarDuplicada() {
    if (!reactivable) return;
    setGuardando(true);
    try {
      await actualizarArea(reactivable.id, { nombre: reactivable.nombre, color, orden: Number(orden), activa: true });
      toast.success(`Área "${reactivable.nombre}" reactivada.`);
      onGuardado();
    } catch (e) {
      console.error('[incorp][areas] reactivarDuplicada', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el área.');
    } finally {
      setGuardando(false);
    }
  }

  async function anadirMiembro(persona) {
    setMiembroBusy(true);
    try {
      const nuevo = await anadirMiembroArea(area.id, persona.id);
      setMiembros((prev) => (prev.some((m) => m.id === nuevo.id) ? prev : [...prev, nuevo]));
    } catch (e) {
      console.error('[incorp][areas] anadirMiembro', e);
      toast.error(cuerpoError(e).message || 'No se pudo añadir el miembro.');
    } finally {
      setMiembroBusy(false);
    }
  }

  async function quitarMiembro(personaId) {
    setMiembroBusy(true);
    try {
      await quitarMiembroArea(area.id, personaId);
      setMiembros((prev) => prev.filter((m) => m.id !== personaId));
    } catch (e) {
      console.error('[incorp][areas] quitarMiembro', e);
      toast.error(cuerpoError(e).message || 'No se pudo quitar el miembro.');
    } finally {
      setMiembroBusy(false);
    }
  }

  const footer = reactivable ? (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={reactivarDuplicada} disabled={guardando}>
        {guardando ? <Loader2 className="animate-spin" /> : <RotateCcw />}Reactivar el área existente
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={guardar} disabled={guardando}>
        {guardando ? <Loader2 className="animate-spin" /> : null}{esEdicion ? 'Guardar cambios' : 'Crear área'}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={esEdicion ? 'Editar área' : 'Nueva área'}
      subtitle={esEdicion ? area.nombre : 'Define nombre, color y orden.'}
      footer={footer}
    >
      <div className="space-y-4">
        <Input
          label="Nombre *"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          error={errNombre || undefined}
          maxLength={60}
          autoFocus
        />

        <div>
          <label className="mb-1.5 block text-xs font-medium text-v25-ink-700">Color</label>
          <ColorPicker value={color} onChange={setColor} disabled={guardando} />
        </div>

        <Input
          label="Orden"
          type="number"
          min={0}
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          hint="Posición en los listados (menor primero)."
          wrapperClassName="max-w-[140px]"
        />

        {/* Aviso de reactivación tras 409 de nombre desactivado */}
        {reactivable && (
          <div className="flex items-start gap-2 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-xs text-v25-received-fg">
            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Ya existe un área <strong>«{reactivable.nombre}»</strong> desactivada. En vez de crear otra,
              puedes reactivarla con los valores de este formulario.
            </span>
          </div>
        )}

        {/* Miembros — solo en edición (el alta necesita el id del área) */}
        <div className="border-t border-v25-line pt-4">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-v25-ink-700">
            <Users className="h-3.5 w-3.5" /> Miembros del área
          </div>
          {esEdicion ? (
            <div className="space-y-2">
              <PersonaPickerMiembro
                onAdd={anadirMiembro}
                excluirIds={miembros.map((m) => m.id)}
                disabled={miembroBusy}
              />
              {miembros.length === 0 ? (
                <p className="text-xs text-v25-ink-400">Sin miembros. Búscalos arriba para añadirlos.</p>
              ) : (
                <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
                  {miembros.map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-2 px-2 py-1.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <Avatar name={m.nombre} size="sm" />
                        <span className="truncate text-sm text-v25-ink-800">{m.nombre}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => quitarMiembro(m.id)}
                        disabled={miembroBusy}
                        className="shrink-0 text-v25-ink-400 hover:text-red-600 disabled:opacity-50"
                        aria-label={`Quitar a ${m.nombre}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-xs text-v25-ink-400">Guarda el área para gestionar sus miembros.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────── Modal danger (desactivar) ───────────────────────────
function DesactivarModal({ area, onClose, onDesactivado }) {
  const [procesando, setProcesando] = useState(false);
  const [bloqueo, setBloqueo] = useState(null); // recursos afectados si el backend devuelve 409

  async function confirmar() {
    setProcesando(true);
    try {
      await desactivarArea(area.id);
      toast.success(`Área "${area.nombre}" desactivada.`);
      onDesactivado();
    } catch (e) {
      const body = cuerpoError(e);
      if (body.error === 'area_con_recursos') {
        setBloqueo(body.recursos || []);
      } else {
        console.error('[incorp][areas] desactivar', e);
        toast.error(body.message || 'No se pudo desactivar el área.');
      }
    } finally {
      setProcesando(false);
    }
  }

  const footer = bloqueo ? (
    <Button variant="outline" onClick={onClose}>Entendido</Button>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={procesando}>Cancelar</Button>
      <Button variant="destructive" onClick={confirmar} disabled={procesando}>
        {procesando ? <Loader2 className="animate-spin" /> : <Ban />}Desactivar
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      tone="danger"
      title={bloqueo ? 'No se puede desactivar' : 'Desactivar área'}
      subtitle={area.nombre}
      footer={footer}
    >
      {bloqueo ? (
        <div className="space-y-3">
          <p className="text-sm text-v25-ink-700">
            Esta área tiene {bloqueo.length} recurso(s) activo(s) que la usan como responsable.
            Reasígnalos o desactívalos antes de desactivar el área.
          </p>
          <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
            {bloqueo.map((r) => (
              <li key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm text-v25-ink-800">
                <PackageOpen className="h-4 w-4 shrink-0 text-v25-ink-400" />
                {r.nombre}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-v25-ink-700">
          El área dejará de estar disponible para nuevas incorporaciones. Podrás reactivarla después.
          ¿Seguro que quieres desactivar <strong>«{area.nombre}»</strong>?
        </p>
      )}
    </Modal>
  );
}
